import { log } from '@ms-ows/logging'

import {
  ActionType,
  HostRenderSurfaceParams,
  MultiSurfaceParams,
} from '../../models'
import { createSurfaceParamsByIndex } from '../../surface-params'
import { performActionBeaconCallback } from '../beacon'
import { removeSurface } from '../remove'
import { getLaunchTime, getPageContext } from '../state'
import { logElapsedEvent } from '../telemetry'

import { renderSingle } from './single'

export const renderMultipleSurfaces = async (
  surfaceParams: MultiSurfaceParams,
  renderCallback:
    | ((surfaceParams: HostRenderSurfaceParams[]) => Promise<(() => void)[]>)
    | undefined,
  renderIndex: number,
  renderTargetId: string,
  surfaceLaunchedEvent?: () => void,
  onNextSurface?: (actionType: ActionType, renderIndex: number) => void,
  onUpdated?: () => void,
  onLastSurfaceClosed?: (actionType: ActionType) => void
) => {
  if (renderIndex > surfaceParams.itemCount) {
    log.assert(renderIndex <= surfaceParams.itemCount, 'inconsistent state')

    return
  }

  await renderSingle(
    createSurfaceParamsByIndex(renderIndex, surfaceParams),
    renderTargetId,
    false, // for multiple surfaces do not removeSurface on target element click
    renderCallback,
    surfaceLaunchedEvent,
    onUpdated,
    async (button: ActionType) => {
      removeSurface(true)

      // Do not show the subsequent UIs in the sequence if the first surface cannot be rendered
      if (button === 'error' && renderIndex <= 1) {
        onLastSurfaceClosed && onLastSurfaceClosed(button)

        return
      }

      logElapsedEvent(
        log.reserve(0x2360288b /* tag_9yc8l */),
        'SurfaceDismissed',
        button,
        renderIndex,
        getLaunchTime(),
        getPageContext().surfaceId,
        surfaceParams.itemCount
      )

      performActionBeaconCallback(button, surfaceParams)

      if (
        renderIndex < surfaceParams.itemCount &&
        (button === 'primary' || button === 'error')
      ) {
        // proceed and show next surface
        await renderMultipleSurfaces(
          surfaceParams,
          renderCallback,
          renderIndex + 1,
          renderTargetId,
          undefined, // logs the event only for the first renderIndex. For the subsequent indices, surfaceLaunchedEvent callback is explicitly set to undefined.
          onNextSurface,
          onUpdated,
          onLastSurfaceClosed
        )

        onNextSurface && onNextSurface(button, renderIndex)
      } else {
        // last surface element or auto-dismiss or close or secondary or
        // upon clicking a non-primary button, the rendering of sequenced teaching ui will be stopped
        onLastSurfaceClosed && onLastSurfaceClosed(button)
      }
    }
  )
}
